/**
 * The device's own copy of the book.
 *
 * CLAUDE.md's first non-negotiable: "The scorebook must never lose an event.
 * Not on a dead network, not on a killed app, not on a phone restart." That
 * sentence is why this file exists and why every write below is awaited before
 * anything else happens.
 *
 * TWO roles in one store, which is the thing to understand before changing it:
 *
 *   - The LOG. Every event this device recorded, append-only, never deleted.
 *     Undo reads it (ADR-0045 bounds undo depth to this device's log, because
 *     no server read returns a voiding row), the screen derives the score and
 *     the clock from it, and it is what "zero data loss" is measured against.
 *   - The OUTBOX. The subset not yet acknowledged by the server, which is
 *     exactly the records with `synced === 0`.
 *
 * They are one store because they hold the same rows and separating them would
 * mean two writes per tap with no transaction spanning both. `synced` is
 * ENVELOPE, not body: the event itself is immutable once written, and
 * corrections are a new VOID row plus a new event, never an edit.
 */

import { openDB, unwrap, type DBSchema, type IDBPDatabase, type IDBPTransaction } from 'idb';
import { GameEventInputSchema, type GameEventInput } from '@good-season/schema';
import { ids } from '@good-season/platform';

import type { SignedInHere } from './owner.js';

/** The largest `client_seq` the column holds. See `allocateSeq`. */
const MAX_CLIENT_SEQ = 2_147_483_647;

export interface LoggedEvent {
  readonly id: string;
  readonly gameId: string;
  /** 0 while the server has not named it in `known`. Only this field mutates. */
  readonly synced: 0 | 1;
  readonly event: GameEventInput;
}

/**
 * Everything the scorebook needs to open with no network.
 *
 * Cached whenever the game is loaded online, read first on every open. Without
 * it a coach arriving at a gym with no signal gets a screen that knows the game
 * id and nothing else: no roster, so no substitutions and no attribution.
 */
export interface CachedGame {
  readonly id: string;
  readonly eventId: string;
  readonly status: 'scheduled' | 'live' | 'final';
  readonly config: unknown;
  /** The client-side half of BUILD_PLAN 5.2, which the server config has no room for. */
  readonly setup: GameSetup;
  readonly roster: readonly RosterPlayer[];
  /**
   * Who set this book up on this phone (#43), which is who it opens for.
   * Absent on a book cached before books carried an owner; see `mayOpen`.
   */
  readonly userId?: string;
  /**
   * The official final score as the SERVER answered a Finish with it, while
   * the book is final; a Reopen clears it. Not what was typed: a second Finish
   * of a final game keeps the first committed score (`finalizeGame`). Nothing
   * reads it yet: the close dialog takes the score from the answer itself.
   * Optional, so a row cached before it existed needs no schema version.
   */
  readonly officialScore?: FinalScore | undefined;
}

/** An official final score, from the server's answer. */
export interface FinalScore {
  readonly us: number;
  readonly them: number;
}

export interface RosterPlayer {
  readonly lastName?: string;
  readonly id: string;
  readonly firstName: string;
  readonly preferredName: string | null;
  readonly jerseyNumber: number | null;
}

/**
 * The rules the INPUT SURFACE needs, which are a superset of the rules the
 * engine needs.
 *
 * `GameConfigSchema` in `packages/schema` is the engine's config and is
 * deliberately narrow: periods, on-court count, running clock. BUILD_PLAN 5.2
 * asks for more than that (a three point line, attendance, a starting five),
 * and none of it changes a derived statistic, so it lives on the device rather
 * than being smuggled into a column the engine reads.
 */
export interface GameSetup {
  readonly regulationPeriods: number;
  readonly regulationLengthMs: number;
  readonly overtimeLengthMs: number;
  readonly onCourtCount: number;
  readonly runningClock: boolean;
  /** The foul rules, mirrored from the config. Zero means the league does not use the rule. */
  readonly disqualifyingFouls: number;
  readonly teamFoulsForBonus: number;
  readonly teamFoulsForDoubleBonus: number;
  /** When false the 3PT buttons are ABSENT, not disabled (BUILD_PLAN 5.2). */
  readonly threePointLine: boolean;
  /** Present today. Absent players are excluded from the substitution surface. */
  readonly presentPlayerIds: readonly string[];
  readonly startingPlayerIds: readonly string[];
}

interface ScorebookDb extends DBSchema {
  events: {
    key: string;
    value: LoggedEvent;
    indexes: { 'by-game': string; 'by-synced': number };
  };
  games: { key: string; value: CachedGame };
  meta: { key: string; value: unknown };
}

const DB_NAME = 'good-season-scorebook';

/**
 * The schema version. Installed devices hold data under version 1, so this
 * moves only together with a new `if (oldVersion < N)` branch in `upgrade`
 * below. Never edit the version 1 branch: it must keep producing exactly the
 * version 1 schema for a device that is upgrading from nothing.
 */
const DB_VERSION = 1;

let handle: Promise<IDBPDatabase<ScorebookDb>> | null = null;

/** The `meta` key of the store's identity. See `storeId` below. */
const STORE_ID_KEY = 'storeId';

/**
 * The identity of the store this page first opened, or null before that.
 *
 * A random id is written into `meta` when the store is created. Every later
 * connection this page opens reads it again: a missing or different id means
 * the store this page held was wiped and something else (this page's own
 * reopen, a new tab, or the other tab after a reload) created a fresh one. The
 * version cannot tell those apart, because a fresh store is at version 1 too.
 * That is what tells a recoverable dropped connection apart from cleared site
 * data.
 */
let storeId: string | null = null;

/**
 * Why this page has stopped using the database, if it has. Sticky for the life
 * of the page, and set only from the connection's own events:
 *
 *  - `superseded`: a newer version of the app took the database. This build
 *    only knows version 1, cannot open a higher one, and only a reload loads
 *    the build that can.
 *  - `unavailable`: the store was wiped under the page. Either the database
 *    was deleted while this page held it (`blocking` with no new version), or
 *    a reopen after a dropped connection found a store whose identity is not
 *    the one this page opened (`checkIdentity`). Using that fresh EMPTY database would make
 *    the outbox read as synced and allow Finish while the screen still showed
 *    plays the server never received. Refusing every call keeps the failure
 *    visible; a reload makes the screen read the disk again, so the two agree.
 *
 * A dropped connection on its own (`terminated`: WebKit loses its IndexedDB
 * server when a phone is backgrounded) is NOT a stop. The next call reopens,
 * and if the same store is still there nothing is lost, and a call that had
 * its connection when it dropped says nothing. A tap that meets the drop while
 * the page is still OPENING (`withDatabase` awaits `opening` outside its retry)
 * is refused visibly with the browser's error, and nothing was written.
 *
 * Both rely on a reload discarding this module's state. On the web it does. A
 * native shell's `navigation.restart` is documented not to
 * (`packages/platform/src/navigation.ts`), so a Capacitor build must clear
 * these another way before it ships.
 */
let stopped: 'superseded' | 'unavailable' | null = null;

/**
 * Whether the `superseded` stop leaves open that the browser also wiped the
 * store. Only a `blocking` stop with a real new version is certain: this page's
 * own live connection was told to upgrade, so the store was there a moment ago.
 * Every `VersionError` is uncertain, whether it came from a reopen after a
 * working connection or from the page's FIRST open (both set it), because a newer build that
 * finds the browser's data cleared can create the store at the higher version
 * before this page ever looks, and this page cannot tell. Set once, with the
 * stop. A certain stop is only certain about the store: a retried append can
 * still have committed before the stop, so the copy never claims a refused
 * entry is absent. A detected wipe (`checkIdentity` finding a different store
 * id) overrides any `superseded` stop, certain or not, because it is known.
 */
let storeMayBeGone = false;

/** The typed error for the current stop, or null while the database is in use. */
function stoppedError(): OutboxSupersededError | OutboxUnavailableError | null {
  if (stopped === 'superseded') return new OutboxSupersededError(storeMayBeGone);
  if (stopped === 'unavailable') return new OutboxUnavailableError();
  return null;
}

/**
 * A newer version of the app opened in another tab and took the database.
 *
 * Every call to this module that has not started its transaction by then
 * rejects with it, instead of the browser's raw `VersionError` or
 * `InvalidStateError`, so the screen can say what happened and what to do. A
 * transaction already started runs to completion, because closing a
 * connection waits for it, and its call keeps its result. The plays already
 * recorded are untouched
 * on disk; this device simply cannot add to them until the page is reloaded
 * onto the newer build.
 *
 * Carries its own six character diagnostic, which `diagnosticFor` passes
 * through as written.
 */
export class OutboxSupersededError extends Error {
  override readonly name = 'OutboxSupersededError';
  readonly diagnostic = 'SB-VER';
  /**
   * True when the stop came from a `VersionError` on an open, whether a reopen
   * after a working connection or the page's first open, so the store may have
   * been wiped and recreated by the newer build. The screen then cannot say the plays are saved on this phone.
   */
  readonly storeMayBeGone: boolean;
  constructor(storeMayBeGone = false) {
    super('a newer version of the app holds the scorebook database');
    this.storeMayBeGone = storeMayBeGone;
  }
}

/**
 * The browser wiped this phone's copy of the book while the page held it.
 *
 * Distinct from `OutboxSupersededError`: no newer build is waiting, and the
 * plays that had not reached the server are gone from this phone. Treated
 * everywhere as a store that is gone, never as an empty one.
 */
export class OutboxUnavailableError extends Error {
  override readonly name = 'OutboxUnavailableError';
  readonly diagnostic = 'SB-GON';
  constructor() {
    super('the browser wiped the scorebook database under this page');
  }
}

/**
 * A write whose outcome this page cannot know.
 *
 * Thrown by `withDatabase` when a write's first attempt failed on a connection
 * that closed under it and the one retry then failed with an error that is
 * not a stop. The first attempt can have committed with only its
 * acknowledgement lost (the case the retry exists for), so the write may or
 * may not be on disk. The screen must neither say nothing changed nor invite a
 * blind retry: a play entered again is a second row with fresh ids, an Undo
 * repeated reverses the first Undo, and a substitution repeated is a second
 * swap. FINAL_REVIEW_55 N1.
 *
 * Not a stop. Nothing sticky is set, and a later call may work. A stop set by
 * the retry (a wipe or a takeover found on the reopen) always wins over this.
 * Carries its own six character diagnostic, like the two stops, and the
 * browser's error as `cause` for a log; neither the cause nor its message
 * reaches a screen.
 */
export class OutboxUncertainError extends Error {
  override readonly name = 'OutboxUncertainError';
  readonly diagnostic = 'SB-UNK';
  constructor(cause: unknown) {
    super('a scorebook write may or may not have committed', { cause });
  }
}

/**
 * Every use of the database goes through here.
 *
 * A connection can close under a call that already holds it: `blocking` closes
 * it for another tab, and the browser can drop it (`terminated`), in which case
 * the call sees the raw `InvalidStateError` from `transaction()` or an
 * `AbortError` from a transaction the close aborted, possibly before the
 * `close` event has been dispatched at all.
 *
 *  - If a stop is set, the stop's typed error is thrown instead, so the screen
 *    says what happened rather than offering a retry that cannot work.
 *  - If the connection is closed and no stop is set, the handle is dropped and
 *    `work` runs ONCE more through `db()`, which reopens (and refuses a wiped
 *    store in `checkIdentity`). A retry cannot write twice, even when a commit
 *    landed and only its acknowledgement was lost: every `work` here is
 *    idempotent by construction. `append` receives its event ids minted before
 *    this call and returns the rows already on disk when they are there, without
 *    allocating a sequence number; every other write puts a fixed value under a
 *    fixed key or sets a flag already set; a read retried is only a read.
 *  - If that retry fails too and `work` writes, the first attempt's outcome is
 *    unknown, so a stop set by now is thrown and otherwise
 *    `OutboxUncertainError`, never the raw error. A read that fails its retry
 *    changed nothing and passes its error through.
 *  - Anything else passes through unchanged.
 */
async function withDatabase<T>(
  work: (database: IDBPDatabase<ScorebookDb>) => Promise<T>,
  writes = false,
): Promise<T> {
  const opening = db();
  const database = await opening;
  try {
    return await work(database);
  } catch (error) {
    const stop = stoppedError();
    if (stop !== null) throw stop;
    if (!isClosed(database)) throw error;
    if (handle === opening) handle = null;
  }
  try {
    return await work(await db());
  } catch (error) {
    throw stoppedError() ?? (writes ? new OutboxUncertainError(error) : error);
  }
}

/**
 * Whether a connection can no longer start a transaction.
 *
 * Asked of the raw connection, so the probe makes no idb promise that could go
 * unobserved; a readonly transaction with no requests commits on its own.
 */
function isClosed(database: IDBPDatabase<ScorebookDb>): boolean {
  try {
    unwrap(database).transaction('meta', 'readonly');
    return false;
  } catch {
    return true;
  }
}

/**
 * The schema steps, additive per version.
 *
 * Exported so the upgrade path can be run from a version that exists. At
 * `DB_VERSION` 1 the browser never calls this with an `oldVersion` above 0, so
 * the `oldVersion < 1` guard is unreachable through `db()` until the first
 * bump; the test drives it directly from an installed version 1 database.
 */
export function upgradeScorebook(database: IDBPDatabase<ScorebookDb>, oldVersion: number): void {
  // A device at version 0 (a fresh install) runs every branch in order; a
  // device at version 1 runs only the new ones. `createObjectStore` throws if
  // the store exists, so an unguarded call is a future version bump that
  // breaks every installed device.
  if (oldVersion < 1) {
    const events = database.createObjectStore('events', { keyPath: 'id' });
    events.createIndex('by-game', 'gameId');
    events.createIndex('by-synced', 'synced');
    database.createObjectStore('games', { keyPath: 'id' });
    database.createObjectStore('meta');
  }
  // Version 2 goes here as `if (oldVersion < 2) { ... }`, additive only.
}

function db(): Promise<IDBPDatabase<ScorebookDb>> {
  const refusal = stoppedError();
  if (refusal !== null) return Promise.reject(refusal);
  // One connection at a time. Every caller shares the one open, so no open is
  // ever started that nobody is holding.
  if (handle !== null) return handle;

  const opening: Promise<IDBPDatabase<ScorebookDb>> = openDB<ScorebookDb>(DB_NAME, DB_VERSION, {
    upgrade(database, oldVersion, _newVersion, transaction) {
      upgradeScorebook(database, oldVersion);
      // A store created here gets its identity in the same transaction. A
      // failed put aborts the upgrade, and the open rejects below.
      if (oldVersion === 0) {
        transaction
          .objectStore('meta')
          .put(ids.uuid(), STORE_ID_KEY)
          .catch(() => undefined);
      }
    },
    // Another tab or a new service worker wants a higher version and is
    // waiting on THIS connection. Closing it is what lets that upgrade
    // proceed; without it the other tab waits forever and the scorebook it
    // opens never loads. Every write below completes before its caller carries
    // on, so no recorded play is lost. This build cannot open the higher
    // version, so it stops using the database (`superseded`) and the screen
    // asks for a reload. A deleted database (`blockedVersion` null) is not a
    // newer app, but it is a wiped store, so it stops the page too
    // (`unavailable`), and a known wipe overrides an earlier superseded stop.
    blocking(_currentVersion, blockedVersion) {
      if (blockedVersion === null) stopped = 'unavailable';
      else if (blockedVersion > DB_VERSION) stopped ??= 'superseded';
      closeHandle(opening);
    },
    // The browser closed the connection without being asked. That alone is
    // not a stop: the next call reopens, and `checkIdentity` tells a wiped
    // store apart from one that is still there.
    terminated() {
      closeHandle(opening);
    },
    // `blocked` is deliberately absent. It fires on the opener when OTHER
    // tabs hold older connections, and the remedy is those tabs' `blocking`
    // handler above. The open stays pending until they close, which is the
    // correct behaviour, and there is nothing this tab could show or do.
  })
    .then(async (database) => {
      try {
        await checkIdentity(database);
      } catch (error: unknown) {
        // Whatever made the check fail (a wiped store, a refused write), this
        // connection is not going to be used, and `blocking` cannot reach it
        // through a rejected promise. Left open it holds a newer build's
        // upgrade in another tab blocked forever.
        database.close();
        throw error;
      }
      return database;
    })
    .catch((error: unknown) => {
      // A failed open is not kept. The next call asks the browser again, which
      // is what makes "Try again" on the screen a real action rather than a
      // replay of the cached refusal. Found by the fresh-context pass.
      if (handle === opening) handle = null;
      // `checkIdentity` found the store wiped.
      if (stopped === 'unavailable') throw new OutboxUnavailableError();
      // The database is already at a higher version than this build knows.
      if (error instanceof Error && error.name === 'VersionError') {
        stopped = 'superseded';
        storeMayBeGone = true;
        throw new OutboxSupersededError(storeMayBeGone);
      }
      throw error;
    });
  handle = opening;
  return opening;
}

/**
 * Refuse a connection to a store that is not the one this page first opened.
 *
 * The first connection a page opens records the store's id. A store an older
 * build created has none, so the first connection mints it, in a readwrite
 * transaction that reads before it writes, so two tabs doing this at once
 * agree on one id. A later connection never mints: a missing or different id
 * there is a wipe, and the page stops. The caller closes the connection on any
 * failure here.
 */
async function checkIdentity(database: IDBPDatabase<ScorebookDb>): Promise<void> {
  const found = await database.get('meta', STORE_ID_KEY);
  let seen = typeof found === 'string' ? found : null;
  if (seen === null && storeId === null) {
    const minted = ids.uuid();
    const transaction = database.transaction('meta', 'readwrite', { durability: 'strict' });
    const again = await transaction.store.get(STORE_ID_KEY);
    if (typeof again === 'string') {
      seen = again;
    } else {
      await transaction.store.put(minted, STORE_ID_KEY);
      seen = minted;
    }
    await transaction.done;
  }
  if (storeId === null) {
    storeId = seen;
    return;
  }
  if (seen !== storeId) {
    // A different store id is a KNOWN wipe. It wins over a `superseded` stop
    // that `blocking` set while this check was in flight, which could only
    // ever say the store may be intact.
    stopped = 'unavailable';
    throw new OutboxUnavailableError();
  }
}

/** Close this connection and forget it, if it is still the cached one. */
function closeHandle(opening: Promise<IDBPDatabase<ScorebookDb>>): void {
  if (handle === opening) handle = null;
  // A failed open has no connection to close, and its failure is already held
  // by whoever called `db()`.
  void opening.then(
    (database) => {
      database.close();
    },
    () => undefined,
  );
}

/** One readwrite put on a store, committed to disk before it resolves. */
async function putDurably(
  storeName: 'games' | 'meta',
  value: unknown,
  key?: string,
): Promise<void> {
  await withDatabase(async (database) => {
    const transaction = database.transaction(storeName, 'readwrite', { durability: 'strict' });
    await Promise.all([transaction.store.put(value as never, key), transaction.done]);
  }, true);
}

/**
 * This device's id, minted once and kept forever.
 *
 * It is what makes two devices scoring the same game distinguishable, so it
 * must survive a restart. Regenerating it would make this device look like a
 * new one to the merge logic on every launch.
 */
export async function deviceId(): Promise<string> {
  const stored = await withDatabase((database) => database.get('meta', 'deviceId'));
  if (typeof stored === 'string') return stored;

  const minted = ids.uuid();
  await putDurably('meta', minted, 'deviceId');
  return minted;
}

/**
 * The next `count` values of `client_seq`, allocated inside the caller's
 * transaction.
 *
 * **Small integers, and never a timestamp.** `client_seq` is int4 and the
 * schema bounds it at 2147483647. A device using `Date.now()` lands around
 * 1.7e12, which the server refuses; the client's instruction on a refusal is to
 * HOLD the batch and retry, so a seq taken from the clock is an outbox that
 * retries the same rejected batch for the rest of the game and lands nothing.
 *
 * Persisted rather than held in memory, so an app kill does not restart it at
 * zero and produce two events with the same sequence.
 *
 * A whole run at once, and one write, so that the loop over the drafts below
 * contains no `await` on this store. An IndexedDB transaction closes when the
 * microtask queue drains with nothing outstanding on it, and a per-draft
 * allocate is the shape that risks it.
 */
async function allocateSeqs(
  transaction: IDBPTransaction<ScorebookDb, ['events', 'meta'], 'readwrite'>,
  count: number,
): Promise<readonly number[]> {
  const store = transaction.objectStore('meta');
  const current = await store.get('seq');

  let next = typeof current === 'number' ? current : 0;
  const seqs: number[] = [];
  for (let index = 0; index < count; index += 1) {
    // Wrapping rather than throwing. Reaching this needs two billion events on
    // one device, and refusing to record a play is never the better failure.
    next = next >= MAX_CLIENT_SEQ ? 1 : next + 1;
    seqs.push(next);
  }

  await store.put(next, 'seq');
  return seqs;
}

/** An event as a caller writes it. The envelope fields are this module's job. */
export type EventBody = Omit<GameEventInput, 'clientSeq' | 'deviceId' | 'id'> & {
  readonly id?: string;
};

/**
 * Write a run of events to disk as ONE transaction, and only then let the
 * caller carry on.
 *
 * The ordering rule from BUILD_PLAN 5.8, stated as an acceptance criterion:
 * every event is written to IndexedDB BEFORE any network call and before the UI
 * updates optimistically. A tap that has reached the screen has already landed.
 *
 * **A pair is written as a pair.** A substitution is `SUB_OUT` and `SUB_IN` at
 * one clock reading, and a correction is a `VOID` and the play that replaces
 * it. Two transactions mean an app kill between them can land half of either:
 * half a substitution takes a player off the floor and never puts one on, and
 * half a correction voids a play whose replacement never existed. Neither is
 * recoverable, because the log is the truth.
 *
 * Validated against the SERVER's schema here, which is the point of that schema
 * living in a shared package. An event the schema would refuse is refused at
 * the door instead: a 422 is a batch the client must hold and retry forever, so
 * one malformed event queued is a game that never syncs. Better to fail the tap
 * than to poison the outbox. The whole run is refused together, since half a
 * pair is what this function exists to prevent. The server keeps a second door
 * the schema does not carry, the append guard on a voiding row (`playBody`
 * says what it refuses); the shapes this client writes are checked against it
 * in `game.test.ts` rather than here.
 */
export async function queueEvents(
  gameId: string,
  drafts: readonly EventBody[],
): Promise<readonly LoggedEvent[]> {
  const device = await deviceId();
  // Minted here, outside the retried work, so a retry carries the same ids and
  // `append` can recognise a run that already landed.
  const identified = drafts.map((draft) => ({ ...draft, id: draft.id ?? ids.uuid() }));
  return withDatabase((database) => append(database, gameId, device, identified), true);
}

async function append(
  database: IDBPDatabase<ScorebookDb>,
  gameId: string,
  device: string,
  drafts: readonly (EventBody & { readonly id: string })[],
): Promise<readonly LoggedEvent[]> {
  const transaction = database.transaction(['events', 'meta'], 'readwrite', {
    // The screen updates when `transaction.done` resolves, so that resolution
    // has to mean the bytes are on disk. Under relaxed durability a battery
    // death can lose a play the coach already saw.
    durability: 'strict',
  });
  // `done` rejects on a failed put (quota), on an abort the browser makes (Safari
  // backgrounding the tab) and on our own abort, and on several paths below
  // nothing awaits it once an earlier await has thrown. One handler here keeps
  // every one of them from surfacing as an unhandled rejection; the paths that
  // do await `done` still see its rejection.
  transaction.done.catch(() => undefined);

  // The run already landed: a retry whose first attempt committed and lost
  // only its acknowledgement. Hand back what is on disk, with its sequence
  // numbers, and write nothing. A run is one transaction, so it is all there
  // or none of it is; a run only partly present was never written by this
  // module, and is refused rather than overwritten.
  const already = await Promise.all(
    drafts.map((draft) => transaction.objectStore('events').get(draft.id)),
  );
  const present = already.filter((row): row is LoggedEvent => row !== undefined);
  if (present.length > 0) {
    if (present.length === drafts.length) {
      await transaction.done;
      return present;
    }
    transaction.abort();
    throw new UnrecordableEvent(['id']);
  }

  return await writeRun(transaction, gameId, device, drafts);
}

/**
 * The tail every append shares: sequence numbers, the server's schema, the
 * puts, and the strict commit. Inside the caller's transaction, with no await
 * that is not a request on it.
 */
async function writeRun(
  transaction: IDBPTransaction<ScorebookDb, ['events', 'meta'], 'readwrite'>,
  gameId: string,
  device: string,
  drafts: readonly (EventBody & { readonly id: string })[],
): Promise<readonly LoggedEvent[]> {
  const seqs = await allocateSeqs(transaction, drafts.length);

  const records: LoggedEvent[] = [];
  for (const [index, draft] of drafts.entries()) {
    const parsed = GameEventInputSchema.safeParse({
      ...draft,
      id: draft.id,
      clientSeq: seqs[index],
      deviceId: device,
    });
    if (!parsed.success) {
      transaction.abort();
      throw new UnrecordableEvent(parsed.error.issues.map((issue) => issue.path.join('.')));
    }
    records.push({ id: parsed.data.id, gameId, synced: 0, event: parsed.data });
  }

  const store = transaction.objectStore('events');
  await Promise.all(records.map((record) => store.put(record)));
  await transaction.done;
  return records;
}

/**
 * An event this device refuses to record, because the server would refuse it
 * forever.
 *
 * Carries field names only. The screen maps it to themed copy; the server's
 * vocabulary never reaches anybody.
 */
export class UnrecordableEvent extends Error {
  override readonly name = 'UnrecordableEvent';
  readonly fields: readonly string[];
  constructor(fields: readonly string[]) {
    super(`event refused locally: ${fields.join(', ')}`);
    this.fields = fields;
  }
}

/** What `appendFrom` found on disk, and what it wrote: `rows` is null when it wrote nothing. */
export interface Appended {
  readonly log: readonly LoggedEvent[];
  readonly rows: readonly LoggedEvent[] | null;
}

/**
 * Read this game's log, decide what to write from it, and write it, as ONE
 * readwrite transaction (ADR-0110).
 *
 * A write whose rows depend on the book (the period and clock a play is
 * stamped with, whether End period still has its period to end) cannot read
 * in one transaction and append in another: another window's append fits
 * between the two, and the rows are then decided from a book that is no longer
 * the one on disk. IndexedDB runs readwrite transactions on one store one at a
 * time, across tabs too, so inside one the book cannot move.
 *
 * `build` is called with the log, oldest first, and must be synchronous: an
 * await that is not a request on this transaction lets it commit early. It
 * returns the bodies to write, or null to write nothing. At most `slots`
 * bodies: the ids are minted before the transaction, so a retry after a lost
 * acknowledgement finds a run that already landed by them, as `queueEvents`
 * does, and hands it back rather than deciding again from a book that now
 * includes it.
 */
export async function appendFrom(
  gameId: string,
  slots: number,
  build: (log: readonly LoggedEvent[]) => readonly EventBody[] | null,
): Promise<Appended> {
  const device = await deviceId();
  const pool = Array.from({ length: slots }, () => ids.uuid());
  return withDatabase(async (database) => {
    const transaction = database.transaction(['events', 'meta'], 'readwrite', {
      durability: 'strict',
    });
    transaction.done.catch(() => undefined);
    const events = transaction.objectStore('events');

    const landed = (await Promise.all(pool.map((id) => events.get(id)))).filter(
      (row): row is LoggedEvent => row !== undefined,
    );
    const log = bySeq(await events.index('by-game').getAll(gameId));
    if (landed.length > 0) {
      await transaction.done;
      return { log, rows: landed };
    }

    const bodies = build(log);
    if (bodies === null) {
      await transaction.done;
      return { log, rows: null };
    }
    if (bodies.length > pool.length) {
      transaction.abort();
      throw new UnrecordableEvent(['id']);
    }
    const drafts = bodies.map((body, index) => ({ ...body, id: pool[index] ?? '' }));
    return { log, rows: await writeRun(transaction, gameId, device, drafts) };
  }, true);
}

function bySeq(rows: LoggedEvent[]): LoggedEvent[] {
  return rows.sort((left, right) => left.event.clientSeq - right.event.clientSeq);
}

/** This device's whole log for a game, oldest first. */
export async function logFor(gameId: string): Promise<readonly LoggedEvent[]> {
  return bySeq(
    await withDatabase((database) => database.getAllFromIndex('events', 'by-game', gameId)),
  );
}

/**
 * How many of THIS game's events the server has not acknowledged yet.
 *
 * Read from disk rather than counted from the screen's copy of the log.
 * `synced` is the one field that mutates and it mutates under the outbox rather
 * than under React, so a count taken from state is stale at exactly the moment
 * it decides whether the book may be closed.
 *
 * Per game, not global. `pendingEvents` spans every game on the device, and a
 * tournament morning where another game is stuck would then block this one's
 * Finish forever.
 */
export async function unsyncedCount(gameId: string): Promise<number> {
  const rows = await logFor(gameId);
  return rows.reduce((total, row) => total + (row.synced === 0 ? 1 : 0), 0);
}

/** Everything not yet acknowledged, across every game, oldest first. */
export async function pendingEvents(): Promise<readonly LoggedEvent[]> {
  const rows = await withDatabase((database) => database.getAllFromIndex('events', 'by-synced', 0));
  return rows.sort((left, right) => left.event.clientSeq - right.event.clientSeq);
}

/**
 * Retire the ids the server named in `known` or `rejected`.
 *
 * Those two, never `accepted`. `accepted` is what THIS call wrote, so an id
 * absent from it means the server already held it, which is the successful
 * outcome of a retry rather than a failure (ADR-0041). `rejected` is an id held
 * by another game, which no retry can change (ADR-0054).
 */
export async function markSynced(eventIds: readonly string[]): Promise<void> {
  if (eventIds.length === 0) return;
  await withDatabase(async (database) => {
    // Relaxed on purpose. Losing this flag to a power cut only re-sends an event
    // the server already holds, which is the idempotent retry of ADR-0041. A
    // strict flush per sync batch would cost battery for no protection.
    const transaction = database.transaction('events', 'readwrite', { durability: 'relaxed' });
    const store = transaction.objectStore('events');
    await Promise.all(
      eventIds.map(async (id) => {
        const row = await store.get(id);
        // The event body is untouched. Only the envelope's flag moves.
        if (row !== undefined) await store.put({ ...row, synced: 1 });
      }),
    );
    await transaction.done;
  }, true);
}

export async function cacheGame(game: CachedGame): Promise<void> {
  await putDurably('games', game);
}

/**
 * Stamp an owner on a cached book that has none (ADR-0090). One transaction,
 * so a `finish()` landing between the read and the write cannot be reverted.
 */
export async function stampOwner(gameId: string, userId: string): Promise<void> {
  await withDatabase(async (database) => {
    const transaction = database.transaction('games', 'readwrite', { durability: 'strict' });
    const game = await transaction.store.get(gameId);
    await Promise.all([
      game !== undefined && game.userId === undefined
        ? transaction.store.put({ ...game, userId })
        : undefined,
      transaction.done,
    ]);
  }, true);
}

/**
 * Set a cached book's status, and the official score a Finish was answered
 * with (cleared when none), in one transaction, so it cannot undo a
 * `stampOwner` landing between the read and the write.
 */
export async function stampStatus(
  gameId: string,
  status: CachedGame['status'],
  officialScore?: FinalScore,
): Promise<void> {
  await withDatabase(async (database) => {
    const transaction = database.transaction('games', 'readwrite', { durability: 'strict' });
    const game = await transaction.store.get(gameId);
    await Promise.all([
      game === undefined ? undefined : transaction.store.put({ ...game, status, officialScore }),
      transaction.done,
    ]);
  }, true);
}

export async function cachedGame(gameId: string): Promise<CachedGame | undefined> {
  return withDatabase((database) => database.get('games', gameId));
}

/**
 * Every book this device holds.
 *
 * A scan, not an index. The store holds the handful of games one device has
 * scored; an index earns its keep when that is hundreds. The start page reads
 * this when it cannot reach the server, so a cold open with no signal can
 * offer the book instead of a dead end (issue #208).
 */
export async function listCachedGames(): Promise<readonly CachedGame[]> {
  return withDatabase((database) => database.getAll('games'));
}

/**
 * The book this device holds for a fixture, if any. Read BEFORE any network
 * call: a coach with no signal has to get back into a book they started, and
 * the fixture route that names the game id is exactly what they cannot reach.
 */
export async function cachedGameForEvent(eventId: string): Promise<CachedGame | undefined> {
  const games = await listCachedGames();
  return games.find((game) => game.eventId === eventId);
}

/**
 * What this device was starting on a fixture when its Start did not come
 * back (ADR-0061).
 *
 * Written BEFORE the fixture POST and cleared once the book is cached. The
 * POST commits on the server whether or not its answer reaches the phone (a
 * captive portal, signal lost mid-request, a reload), and without this the
 * device that started the game came back to the fixture, found a game it
 * did not hold, and was refused as a second device. With it, setup resumes
 * from here into the game the server answers with. Found by the
 * fresh-context pass.
 */
export interface StartingGame {
  readonly setup: GameSetup;
  readonly roster: readonly RosterPlayer[];
}

const startingKey = (eventId: string): string => `starting:${eventId}`;

export async function markStarting(eventId: string, starting: StartingGame): Promise<void> {
  await putDurably('meta', starting, startingKey(eventId));
}

export async function startingFor(eventId: string): Promise<StartingGame | undefined> {
  const stored: unknown = await withDatabase((database) =>
    database.get('meta', startingKey(eventId)),
  );
  // Written by `markStarting` only, so the shape check is against this
  // module's own earlier self, not against a server.
  return typeof stored === 'object' && stored !== null && 'setup' in stored && 'roster' in stored
    ? (stored as StartingGame)
    : undefined;
}

export async function clearStarting(eventId: string): Promise<void> {
  // Default durability on purpose: losing the delete leaves a stale marker that
  // setup resumes from and then clears, which costs nothing.
  await withDatabase((database) => database.delete('meta', startingKey(eventId)), true);
}

/**
 * Who this phone last knew to be signed in (#43), for `mayOpen`.
 *
 * Written whenever the server names the signed-in person, and set to null on
 * sign-out or when the server says the session is gone, so a book refuses the
 * next person on the phone even with no signal to ask the server who they are.
 */
const SIGNED_IN = 'signedIn';

/**
 * A sign-out this phone has recorded and the server has not yet confirmed
 * (#43, ADR-0090). The session cookie is HttpOnly, so only the server can end
 * the session: until it says so, `api()` sends the sign-out before anything
 * else and treats the phone as signed out. Set by `forgetSignedIn`.
 */
const SIGN_OUT_PENDING = 'signOutPending';

export async function rememberSignedIn(userId: string | null): Promise<void> {
  await putDurably('meta', userId, SIGNED_IN);
}

export async function signedInHere(): Promise<SignedInHere> {
  const stored: unknown = await withDatabase((database) => database.get('meta', SIGNED_IN));
  return typeof stored === 'string' || stored === null ? stored : undefined;
}

/**
 * Sign-out's half of the book (#43, ADR-0090): forget who was signed in,
 * record that the server still has to be told (`signOutPending`), and drop
 * the cached books of FINISHED games, which carry the roster and have nothing
 * left to score.
 *
 * A live or scheduled book stays, closed by `mayOpen` to anybody but the
 * person who set it up, so a coach who signs out at half time can sign back
 * in and carry on. A finished book still holding an unsent play stays too,
 * because the book is the way back to the reopen that lands it (ADR-0047).
 *
 * Setup markers (`markStarting`) stay: a Start whose answer was lost is only
 * resumable from one (ADR-0061), and it is read only after the server has
 * answered the fixture for whoever is signed in, so it shows nobody else
 * anything.
 *
 * NEVER touches `events`. One transaction, so the read of what is unsent and
 * the delete cannot disagree about a play written between.
 *
 * Resolves to how many plays are still unsent.
 */
export async function forgetSignedIn(): Promise<number> {
  return withDatabase(async (database) => {
    const transaction = database.transaction(['events', 'games', 'meta'], 'readwrite', {
      durability: 'strict',
    });
    // Every write is observed together with `done`, so a rejected put cannot leave the abort unhandled.
    const writes: Promise<unknown>[] = [transaction.done];
    // Written with the null, so no reader sees one without the other.
    writes.push(transaction.objectStore('meta').put(null, SIGNED_IN));
    writes.push(transaction.objectStore('meta').put(true, SIGN_OUT_PENDING));
    const unsent = await transaction.objectStore('events').index('by-synced').getAll(0);
    const holding = new Set(unsent.map((row) => row.gameId));
    const games = transaction.objectStore('games');
    for (const game of await games.getAll()) {
      if (game.status === 'final' && !holding.has(game.id)) writes.push(games.delete(game.id));
    }
    await Promise.all(writes);
    return unsent.length;
  }, true);
}

export async function signOutPending(): Promise<boolean> {
  return (await withDatabase((database) => database.get('meta', SIGN_OUT_PENDING))) === true;
}

export async function clearSignOutPending(): Promise<void> {
  await withDatabase((database) => database.delete('meta', SIGN_OUT_PENDING), true);
}
